import { supabase } from '../state/auth/supabaseClient';
import { getAmountPaidAtCheckout } from '../utils/orderPayment';
import { DEFAULT_CLOSED_MESSAGE, normalizeOpeningHours } from '../utils/openingHours';
import {
  BundleType,
  CategoryRow,
  DealRow,
  DealSlotRow,
  DealWithSlots,
  DiscountCodeRow,
  DiscountValidationResult,
  FlavourChoiceListWithOptions,
  FlavourChoiceOptionRow,
  MenuItemDrinkOption,
  MenuItemRow,
  OrderRow,
  OrderStatus,
  PaymentStatus,
  InStorePaymentMethod,
  OrderItemRowLite,
  OrderItemSelectionsPayload,
  UserRow,
  HeroPromoSettings,
  HeroDealImageSettings,
  MenuBackgroundImageSettings,
  SaladSaucePreferenceListWithOptions,
  SaladSaucePreferenceOptionRow,
  OrdersPauseSettings,
  OrdersPauseStatus,
  OrderingAvailability,
  OpeningHoursSettings,
} from '../types/app';

function assertNoError(error: { message: string } | null): void {
  if (!error) return;
  throw new Error(error.message);
}

export async function uploadMenuItemImage(file: File): Promise<string> {
  const bucket = process.env.REACT_APP_SUPABASE_MENU_IMAGES_BUCKET || 'menu-images';
  const ext = file.name.split('.').pop() || 'jpg';
  // Store at the bucket root so the returned URL does not include `menu-items/`.
  const filePath = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file, {
    cacheControl: '3600',
    upsert: false,
  });
  assertNoError(uploadError);

  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  if (!data?.publicUrl) {
    throw new Error('Failed to resolve public image URL after upload.');
  }
  return data.publicUrl;
}

function normalizeMenuItemImageUrl(imageUrl: string): string {
  // Existing DB rows may have older keys like: /object/menu-images/menu-items/<file>
  // New upload keys remove that `menu-items/` prefix.
  return imageUrl.replace(
    '/storage/v1/object/menu-images/menu-items/',
    '/storage/v1/object/menu-images/'
  );
}

export async function getMyOrders(userId: string): Promise<OrderRow[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  assertNoError(error);
  return (data ?? []) as OrderRow[];
}

export async function getAllOrders(): Promise<OrderRow[]> {
  const { data, error } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
  assertNoError(error);
  return (data ?? []) as OrderRow[];
}

export async function updateOrderStatus(id: string, status: OrderStatus): Promise<void> {
  const completedAt = status === 'Complete' ? new Date().toISOString() : null;
  const { error } = await supabase
    .from('orders')
    .update({ order_status: status, completed_at: completedAt })
    .eq('id', id);
  assertNoError(error);
}

export async function updateOrderPaymentStatus(id: string, paymentStatus: PaymentStatus): Promise<void> {
  if (paymentStatus === 'paid') {
    const { data: order, error: readError } = await supabase
      .from('orders')
      .select('total_amount')
      .eq('id', id)
      .single();
    assertNoError(readError);
    const total = roundMoney(Number(order?.total_amount) || 0);
    const { error } = await supabase
      .from('orders')
      .update({ payment_status: paymentStatus, amount_paid_at_checkout: total })
      .eq('id', id);
    assertNoError(error);
    return;
  }

  const { error } = await supabase
    .from('orders')
    .update({ payment_status: paymentStatus, amount_paid_at_checkout: 0 })
    .eq('id', id);
  assertNoError(error);
}

export async function updateOrderPaymentMethod(id: string, paymentMethod: InStorePaymentMethod): Promise<void> {
  const { error } = await supabase.from('orders').update({ payment_method: paymentMethod }).eq('id', id);
  assertNoError(error);
}

export async function updateOrderEstimatedPickup(id: string, estimatedPickup: string | null): Promise<void> {
  const { error } = await supabase
    .from('orders')
    .update({ estimated_pickup: estimatedPickup })
    .eq('id', id);
  assertNoError(error);
}

/** Queue (or re-queue) a kitchen label via the local print server (clears label_printed_at). */
export async function requestOrderLabelPrint(id: string): Promise<void> {
  const { error } = await supabase.from('orders').update({ label_printed_at: null }).eq('id', id);
  assertNoError(error);
}

export type AmendOrderLineInput = {
  id: string;
  quantity: number;
};

export type AmendOrderNewItemInput = {
  menu_item_id: string;
  quantity: number;
  price: number;
  selections?: OrderItemSelectionsPayload | null;
};

export type AmendOrderNewDealInput = {
  deal_id: string;
  quantity: number;
  price: number;
  selections: OrderItemSelectionsPayload;
};

export async function amendOrderLines(
  orderId: string,
  lines: AmendOrderLineInput[],
  newItems: AmendOrderNewItemInput[] = [],
  newDeals: AmendOrderNewDealInput[] = []
): Promise<OrderRow> {
  const { data: order, error: orderError } = await supabase.from('orders').select('*').eq('id', orderId).single();
  assertNoError(orderError);
  if (!order) throw new Error('Order not found');

  const status = order.order_status as OrderStatus;
  if (status === 'Complete' || status === 'Cancelled') {
    throw new Error('Cannot amend a completed or cancelled order.');
  }

  const { data: existingItems, error: itemsError } = await supabase
    .from('order_items')
    .select('id')
    .eq('order_id', orderId);
  assertNoError(itemsError);

  const existingIds = new Set((existingItems ?? []).map((row) => row.id as string));
  if (!existingIds.size && !newItems.some((item) => item.quantity > 0) && !newDeals.some((item) => item.quantity > 0)) {
    throw new Error('This order has no items to amend.');
  }

  for (const line of lines) {
    if (!existingIds.has(line.id)) {
      throw new Error('One or more items no longer belong to this order.');
    }
    if (line.quantity <= 0) {
      const { error } = await supabase.from('order_items').delete().eq('id', line.id);
      assertNoError(error);
      existingIds.delete(line.id);
    } else {
      const { error } = await supabase.from('order_items').update({ quantity: line.quantity }).eq('id', line.id);
      assertNoError(error);
    }
  }

  const itemsToInsert = [
    ...newItems.filter((item) => item.quantity > 0),
    ...newDeals.filter((item) => item.quantity > 0),
  ];
  if (!existingIds.size && !itemsToInsert.length) {
    throw new Error('An order must keep at least one item.');
  }

  if (itemsToInsert.length) {
    const { error: insertError } = await supabase.from('order_items').insert([
      ...newItems
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          order_id: orderId,
          menu_item_id: item.menu_item_id,
          deal_id: null,
          quantity: item.quantity,
          price: item.price,
          selections: item.selections ?? null,
        })),
      ...newDeals
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          order_id: orderId,
          menu_item_id: null,
          deal_id: item.deal_id,
          quantity: item.quantity,
          price: item.price,
          selections: item.selections,
        })),
    ]);
    assertNoError(insertError);
  }

  const { data: updatedItems, error: reloadError } = await supabase
    .from('order_items')
    .select('quantity, price')
    .eq('order_id', orderId);
  assertNoError(reloadError);

  if (!updatedItems?.length) {
    throw new Error('An order must keep at least one item.');
  }

  const subtotal = updatedItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
    0
  );
  const discount = Number(order.discount_amount) || 0;
  const newTotal = Math.max(0, roundMoney(subtotal - discount));

  const { data: updatedOrder, error: updateError } = await supabase
    .from('orders')
    .update({
      total_amount: newTotal,
      label_printed_at: null,
    })
    .eq('id', orderId)
    .select('*')
    .single();
  assertNoError(updateError);
  if (!updatedOrder) throw new Error('Failed to update order.');

  return updatedOrder as OrderRow;
}

export async function recordAdditionalPaymentCollected(
  orderId: string,
  paymentMethod: InStorePaymentMethod
): Promise<OrderRow> {
  const { data: order, error: orderError } = await supabase.from('orders').select('*').eq('id', orderId).single();
  assertNoError(orderError);
  if (!order) throw new Error('Order not found');

  const orderRow = order as OrderRow;
  const total = roundMoney(Number(orderRow.total_amount) || 0);
  const paid = getAmountPaidAtCheckout(orderRow);
  const outstanding = roundMoney(Math.max(0, total - paid));
  if (outstanding < 0.01) {
    throw new Error('No additional payment is due on this order.');
  }

  const { data: updatedOrder, error: updateError } = await supabase
    .from('orders')
    .update({
      amount_paid_at_checkout: total,
      payment_method: paymentMethod,
      payment_status: 'paid',
    })
    .eq('id', orderId)
    .select('*')
    .single();
  assertNoError(updateError);
  if (!updatedOrder) throw new Error('Failed to record payment.');

  return updatedOrder as OrderRow;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export async function getAllUsers(): Promise<UserRow[]> {
  const { data, error } = await supabase.from('users').select('*').order('email');
  assertNoError(error);
  return (data ?? []) as UserRow[];
}

function normalizeCategoryRow(c: CategoryRow & { is_archived?: boolean }): CategoryRow {
  return {
    ...c,
    is_archived: c.is_archived ?? false,
  };
}

export async function getCategories(): Promise<CategoryRow[]> {
  const { data, error } = await supabase.from('categories').select('*').order('display_order').order('name');
  assertNoError(error);
  return (data ?? []).map((c) => normalizeCategoryRow(c as CategoryRow)).filter((c) => !c.is_archived);
}

export async function createCategory(name: string, displayOrder: number): Promise<void> {
  const safeOrder = Math.max(1, Math.floor(displayOrder || 1));
  const { error } = await supabase.from('categories').insert({ name, display_order: safeOrder });
  assertNoError(error);
}

export async function updateCategory(id: string, name: string, displayOrder: number): Promise<void> {
  const safeOrder = Math.max(1, Math.floor(displayOrder || 1));
  const { error } = await supabase
    .from('categories')
    .update({ name, display_order: safeOrder })
    .eq('id', id);
  if (error?.message?.includes('display_order')) {
    throw new Error('display_order column is missing in Supabase. Please run the latest schema.sql first.');
  }
  assertNoError(error);
}

async function categoryHasMenuItems(categoryId: string): Promise<boolean> {
  const { data, error } = await supabase.from('menu_items').select('id').eq('category_id', categoryId).limit(1);
  assertNoError(error);
  return (data?.length ?? 0) > 0;
}

async function archiveMenuItemsInCategory(categoryId: string): Promise<void> {
  const { error } = await supabase
    .from('menu_items')
    .update({ is_archived: true, is_available: false })
    .eq('category_id', categoryId);

  if (error?.message?.includes('is_archived')) {
    const { error: hideError } = await supabase
      .from('menu_items')
      .update({ is_available: false })
      .eq('category_id', categoryId);
    assertNoError(hideError);
    return;
  }

  assertNoError(error);
}

async function archiveCategory(id: string): Promise<void> {
  await archiveMenuItemsInCategory(id);

  const { data, error } = await supabase.from('categories').update({ is_archived: true }).eq('id', id).select('id');

  if (error?.message?.includes('is_archived')) {
    throw new Error('Category could not be removed. Please run the latest schema.sql in Supabase first.');
  }

  assertNoError(error);
  if (!data?.length) {
    throw new Error('Category could not be removed from the menu.');
  }
}

export type CategoryDeleteResult = 'deleted' | 'archived';

export async function deleteCategory(id: string): Promise<CategoryDeleteResult> {
  if (await categoryHasMenuItems(id)) {
    await archiveCategory(id);
    return 'archived';
  }

  const { data, error } = await supabase.from('categories').delete().eq('id', id).select('id');
  if (!error && data?.length) return 'deleted';

  if (isForeignKeyDeleteError(error)) {
    await archiveCategory(id);
    return 'archived';
  }

  if (error) throw formatDeleteError(error, 'category');
  throw new Error('Category could not be deleted. It may have already been removed or you may not have permission.');
}

function normalizeMenuItemRow(
  m: MenuItemRow & {
    description?: string | null;
    image_url?: string | null;
    is_sold_out?: boolean;
    hide_from_menu?: boolean;
    is_archived?: boolean;
    includes_drink_choice?: boolean;
    eligible_for_discount?: boolean;
    offers_meal_upgrade?: boolean;
    offers_salad_sauce_choice?: boolean;
    salad_sauce_preference_list_id?: string | null;
    flavour_choice_list_id?: string | null;
  }
): MenuItemRow {
  return {
    ...m,
    description: m.description ?? '',
    image_url: m.image_url ? normalizeMenuItemImageUrl(m.image_url) : null,
    is_sold_out: m.is_sold_out ?? false,
    hide_from_menu: m.hide_from_menu ?? false,
    is_archived: m.is_archived ?? false,
    includes_drink_choice: m.includes_drink_choice ?? false,
    eligible_for_discount: m.eligible_for_discount ?? true,
    offers_meal_upgrade: m.offers_meal_upgrade ?? false,
    offers_salad_sauce_choice: m.offers_salad_sauce_choice ?? false,
    salad_sauce_preference_list_id: m.salad_sauce_preference_list_id ?? null,
    flavour_choice_list_id: m.flavour_choice_list_id ?? null,
    drink_option_ids: m.drink_option_ids ?? [],
  };
}

async function fetchDrinkOptionIdsByMenuItemId(): Promise<Map<string, string[]>> {
  const { data, error } = await supabase
    .from('menu_item_drink_options')
    .select('menu_item_id, drink_menu_item_id, display_order')
    .order('display_order');
  assertNoError(error);
  const byMenuItemId = new Map<string, string[]>();
  (data ?? []).forEach((row) => {
    const menuItemId = row.menu_item_id as string;
    const drinkId = row.drink_menu_item_id as string;
    const list = byMenuItemId.get(menuItemId) ?? [];
    list.push(drinkId);
    byMenuItemId.set(menuItemId, list);
  });
  return byMenuItemId;
}

async function fetchMenuItemRows(): Promise<MenuItemRow[]> {
  const [{ data, error }, drinkOptionsByMenuItemId] = await Promise.all([
    supabase.from('menu_items').select('*').order('name'),
    fetchDrinkOptionIdsByMenuItemId(),
  ]);
  assertNoError(error);
  return (data ?? [])
    .map((m) => {
      const row = normalizeMenuItemRow(m as MenuItemRow);
      return {
        ...row,
        drink_option_ids: drinkOptionsByMenuItemId.get(row.id) ?? [],
      };
    })
    .filter((m) => !m.is_archived);
}

export async function getMenuItems(): Promise<MenuItemRow[]> {
  return (await fetchMenuItemRows()).filter((m) => !m.hide_from_menu);
}

export async function getAdminMenuItems(): Promise<MenuItemRow[]> {
  return fetchMenuItemRows();
}

export async function getDealMenuItems(): Promise<MenuItemRow[]> {
  return fetchMenuItemRows();
}

export async function getMenuItem(id: string): Promise<MenuItemRow | null> {
  const { data, error } = await supabase.from('menu_items').select('*').eq('id', id).maybeSingle();
  assertNoError(error);
  if (!data) return null;
  const row = normalizeMenuItemRow(data as MenuItemRow);
  if (row.is_archived || row.hide_from_menu) return null;
  const drinkOptionsByMenuItemId = await fetchDrinkOptionIdsByMenuItemId();
  return {
    ...row,
    drink_option_ids: drinkOptionsByMenuItemId.get(row.id) ?? [],
  };
}

export async function getMenuItemDrinkOptions(menuItemId: string): Promise<MenuItemDrinkOption[]> {
  const { data: links, error } = await supabase
    .from('menu_item_drink_options')
    .select('drink_menu_item_id, display_order')
    .eq('menu_item_id', menuItemId)
    .order('display_order');
  assertNoError(error);
  const drinkIds = (links ?? []).map((row) => row.drink_menu_item_id as string);
  if (!drinkIds.length) return [];

  const { data: items, error: itemsError } = await supabase
    .from('menu_items')
    .select('id, name, is_available, is_sold_out')
    .in('id', drinkIds);
  assertNoError(itemsError);
  const byId = new Map(
    (items ?? []).map((item) => [
      item.id as string,
      {
        id: item.id as string,
        name: item.name as string,
        isSoldOut: Boolean(item.is_sold_out) || !item.is_available,
      },
    ])
  );
  return drinkIds.map((id) => byId.get(id)).filter((option): option is MenuItemDrinkOption => !!option);
}

export async function getFlavourChoiceLists(): Promise<FlavourChoiceListWithOptions[]> {
  const [{ data: lists, error: listsError }, { data: options, error: optionsError }] = await Promise.all([
    supabase.from('flavour_choice_lists').select('*').order('name'),
    supabase.from('flavour_choice_options').select('*').order('display_order').order('label'),
  ]);
  assertNoError(listsError);
  assertNoError(optionsError);
  const optionsByListId = new Map<string, FlavourChoiceOptionRow[]>();
  (options ?? []).forEach((row) => {
    const listId = row.flavour_choice_list_id as string;
    const current = optionsByListId.get(listId) ?? [];
    current.push({
      id: row.id as string,
      flavour_choice_list_id: listId,
      label: row.label as string,
      display_order: Number(row.display_order) || 1,
      created_at: (row.created_at as string) ?? undefined,
    });
    optionsByListId.set(listId, current);
  });
  return (lists ?? []).map((list) => ({
    id: list.id as string,
    name: list.name as string,
    created_at: (list.created_at as string) ?? undefined,
    updated_at: (list.updated_at as string) ?? undefined,
    options: optionsByListId.get(list.id as string) ?? [],
  }));
}

export async function createFlavourChoiceList(name: string, labels: string[]): Promise<void> {
  const cleanName = name.trim();
  const cleanLabels = labels.map((label) => label.trim()).filter(Boolean);
  if (!cleanName) throw new Error('List name is required.');
  if (!cleanLabels.length) throw new Error('At least one flavour option is required.');
  const { data, error } = await supabase
    .from('flavour_choice_lists')
    .insert({ name: cleanName })
    .select('id')
    .single();
  assertNoError(error);
  if (!data?.id) throw new Error('Could not create flavour list.');
  const rows = cleanLabels.map((label, index) => ({
    flavour_choice_list_id: data.id as string,
    label,
    display_order: index + 1,
  }));
  const { error: optionError } = await supabase.from('flavour_choice_options').insert(rows);
  assertNoError(optionError);
}

export async function updateFlavourChoiceList(listId: string, name: string, labels: string[]): Promise<void> {
  const cleanName = name.trim();
  const cleanLabels = labels.map((label) => label.trim()).filter(Boolean);
  if (!cleanName) throw new Error('List name is required.');
  if (!cleanLabels.length) throw new Error('At least one flavour option is required.');
  const { error: listError } = await supabase.from('flavour_choice_lists').update({ name: cleanName }).eq('id', listId);
  assertNoError(listError);
  const { error: deleteError } = await supabase.from('flavour_choice_options').delete().eq('flavour_choice_list_id', listId);
  assertNoError(deleteError);
  const rows = cleanLabels.map((label, index) => ({
    flavour_choice_list_id: listId,
    label,
    display_order: index + 1,
  }));
  const { error: optionError } = await supabase.from('flavour_choice_options').insert(rows);
  assertNoError(optionError);
}

export async function deleteFlavourChoiceList(listId: string): Promise<void> {
  const { error } = await supabase.from('flavour_choice_lists').delete().eq('id', listId);
  assertNoError(error);
}

export async function getMenuItemFlavourChoices(menuItemId: string): Promise<Array<{ value: string; label: string }>> {
  const { data: item, error: itemError } = await supabase
    .from('menu_items')
    .select('flavour_choice_list_id')
    .eq('id', menuItemId)
    .maybeSingle();
  assertNoError(itemError);
  const listId = item?.flavour_choice_list_id as string | null | undefined;
  if (!listId) return [];
  const { data, error } = await supabase
    .from('flavour_choice_options')
    .select('id, label, display_order')
    .eq('flavour_choice_list_id', listId)
    .order('display_order')
    .order('label');
  assertNoError(error);
  return (data ?? []).map((row) => ({
    value: row.id as string,
    label: row.label as string,
  }));
}

export async function getSaladSaucePreferenceLists(): Promise<SaladSaucePreferenceListWithOptions[]> {
  const [{ data: lists, error: listsError }, { data: options, error: optionsError }] = await Promise.all([
    supabase.from('salad_sauce_preference_lists').select('*').order('name'),
    supabase.from('salad_sauce_preference_options').select('*').order('display_order').order('label'),
  ]);
  assertNoError(listsError);
  assertNoError(optionsError);
  const optionsByListId = new Map<string, SaladSaucePreferenceOptionRow[]>();
  (options ?? []).forEach((row) => {
    const listId = row.salad_sauce_preference_list_id as string;
    const current = optionsByListId.get(listId) ?? [];
    current.push({
      id: row.id as string,
      salad_sauce_preference_list_id: listId,
      label: row.label as string,
      display_order: Number(row.display_order) || 1,
      created_at: (row.created_at as string) ?? undefined,
    });
    optionsByListId.set(listId, current);
  });
  return (lists ?? []).map((list) => ({
    id: list.id as string,
    name: list.name as string,
    created_at: (list.created_at as string) ?? undefined,
    updated_at: (list.updated_at as string) ?? undefined,
    options: optionsByListId.get(list.id as string) ?? [],
  }));
}

export async function createSaladSaucePreferenceList(name: string, labels: string[]): Promise<void> {
  const cleanName = name.trim();
  const cleanLabels = labels.map((label) => label.trim()).filter(Boolean);
  if (!cleanName) throw new Error('List name is required.');
  if (!cleanLabels.length) throw new Error('At least one option is required.');
  const { data, error } = await supabase
    .from('salad_sauce_preference_lists')
    .insert({ name: cleanName })
    .select('id')
    .single();
  assertNoError(error);
  if (!data?.id) throw new Error('Could not create salad & sauces list.');
  const rows = cleanLabels.map((label, index) => ({
    salad_sauce_preference_list_id: data.id as string,
    label,
    display_order: index + 1,
  }));
  const { error: optionError } = await supabase.from('salad_sauce_preference_options').insert(rows);
  assertNoError(optionError);
}

export async function updateSaladSaucePreferenceList(listId: string, name: string, labels: string[]): Promise<void> {
  const cleanName = name.trim();
  const cleanLabels = labels.map((label) => label.trim()).filter(Boolean);
  if (!cleanName) throw new Error('List name is required.');
  if (!cleanLabels.length) throw new Error('At least one option is required.');
  const { error: listError } = await supabase
    .from('salad_sauce_preference_lists')
    .update({ name: cleanName })
    .eq('id', listId);
  assertNoError(listError);
  const { error: deleteError } = await supabase
    .from('salad_sauce_preference_options')
    .delete()
    .eq('salad_sauce_preference_list_id', listId);
  assertNoError(deleteError);
  const rows = cleanLabels.map((label, index) => ({
    salad_sauce_preference_list_id: listId,
    label,
    display_order: index + 1,
  }));
  const { error: optionError } = await supabase.from('salad_sauce_preference_options').insert(rows);
  assertNoError(optionError);
}

export async function deleteSaladSaucePreferenceList(listId: string): Promise<void> {
  const { error } = await supabase.from('salad_sauce_preference_lists').delete().eq('id', listId);
  assertNoError(error);
}

export async function getMenuItemSaladSaucePreferences(
  menuItemId: string
): Promise<Array<{ value: string; label: string }>> {
  const { data: item, error: itemError } = await supabase
    .from('menu_items')
    .select('salad_sauce_preference_list_id')
    .eq('id', menuItemId)
    .maybeSingle();
  assertNoError(itemError);
  const listId = item?.salad_sauce_preference_list_id as string | null | undefined;
  if (!listId) return [];
  const { data, error } = await supabase
    .from('salad_sauce_preference_options')
    .select('id, label, display_order')
    .eq('salad_sauce_preference_list_id', listId)
    .order('display_order')
    .order('label');
  assertNoError(error);
  return (data ?? []).map((row) => ({
    value: row.id as string,
    label: row.label as string,
  }));
}

export async function setMenuItemDrinkOptions(
  menuItemId: string,
  includesDrinkChoice: boolean,
  drinkMenuItemIds: string[]
): Promise<void> {
  const { error: updateError } = await supabase
    .from('menu_items')
    .update({ includes_drink_choice: includesDrinkChoice })
    .eq('id', menuItemId);
  assertNoError(updateError);

  const { error: deleteError } = await supabase.from('menu_item_drink_options').delete().eq('menu_item_id', menuItemId);
  assertNoError(deleteError);

  if (!includesDrinkChoice || !drinkMenuItemIds.length) return;

  const rows = drinkMenuItemIds.map((drink_menu_item_id, index) => ({
    menu_item_id: menuItemId,
    drink_menu_item_id,
    display_order: index + 1,
  }));
  const { error: insertError } = await supabase.from('menu_item_drink_options').insert(rows);
  assertNoError(insertError);
}

export async function createMenuItem(payload: Omit<MenuItemRow, 'id'>): Promise<void> {
  const { error } = await supabase.from('menu_items').insert(payload);
  assertNoError(error);
}

export async function updateMenuItem(id: string, payload: Omit<MenuItemRow, 'id'>): Promise<void> {
  const { error } = await supabase.from('menu_items').update(payload).eq('id', id);
  assertNoError(error);
}

function isForeignKeyDeleteError(error: { code?: string; message: string } | null): boolean {
  if (!error) return false;
  return (
    error.code === '23503' ||
    error.message.includes('violates foreign key') ||
    error.message.includes('is still referenced')
  );
}

function formatDeleteError(error: { message: string }, entityLabel: string): Error {
  if (isForeignKeyDeleteError(error)) {
    return new Error(`Cannot delete this ${entityLabel} because it is referenced by existing orders.`);
  }
  return new Error(error.message);
}

async function menuItemHasOrderHistory(id: string): Promise<boolean> {
  const { data, error } = await supabase.from('order_items').select('id').eq('menu_item_id', id).limit(1);
  assertNoError(error);
  return (data?.length ?? 0) > 0;
}

async function archiveMenuItem(id: string): Promise<void> {
  const { data, error } = await supabase
    .from('menu_items')
    .update({ is_archived: true, is_available: false })
    .eq('id', id)
    .select('id');

  if (error?.message?.includes('is_archived')) {
    const { data: hidden, error: hideError } = await supabase
      .from('menu_items')
      .update({ is_available: false })
      .eq('id', id)
      .select('id');
    assertNoError(hideError);
    if (!hidden?.length) {
      throw new Error('Menu item could not be removed from the menu.');
    }
    return;
  }

  assertNoError(error);
  if (!data?.length) {
    throw new Error('Menu item could not be removed from the menu.');
  }
}

export type MenuItemDeleteResult = 'deleted' | 'archived';

export async function deleteMenuItem(id: string): Promise<MenuItemDeleteResult> {
  if (await menuItemHasOrderHistory(id)) {
    await archiveMenuItem(id);
    return 'archived';
  }

  const { data, error } = await supabase.from('menu_items').delete().eq('id', id).select('id');
  if (!error && data?.length) return 'deleted';

  if (isForeignKeyDeleteError(error)) {
    await archiveMenuItem(id);
    return 'archived';
  }

  if (error) throw formatDeleteError(error, 'menu item');
  throw new Error('Menu item could not be deleted. It may have already been removed or you may not have permission.');
}

export async function getDiscountCodes(): Promise<DiscountCodeRow[]> {
  const { data, error } = await supabase.from('discount_codes').select('*').order('created_at', { ascending: false });
  assertNoError(error);
  return (data ?? []) as DiscountCodeRow[];
}

export async function validateDiscountCode(code: string, subtotal: number): Promise<DiscountValidationResult> {
  const normalized = code.trim().toUpperCase();
  if (!normalized) {
    throw new Error('Enter a promo code.');
  }
  const { data, error } = await supabase
    .from('discount_codes')
    .select('*')
    .eq('code', normalized)
    .maybeSingle();
  assertNoError(error);
  if (!data) throw new Error('Promo code not found.');
  const row = data as DiscountCodeRow;
  if (!row.is_active) throw new Error('Promo code is not active.');
  if (row.expiry_date && new Date(row.expiry_date).getTime() < Date.now()) {
    throw new Error('Promo code has expired.');
  }

  let discount = 0;
  if (row.type === 'percentage') {
    discount = (subtotal * Number(row.value || 0)) / 100;
  } else {
    discount = Number(row.value || 0);
  }
  discount = Math.max(0, Math.min(subtotal, discount));
  discount = Math.round(discount * 100) / 100;

  return { code: row.code, discountAmount: discount };
}

export async function createDiscountCode(payload: Omit<DiscountCodeRow, 'id' | 'created_at'>): Promise<void> {
  const { error } = await supabase.from('discount_codes').insert(payload);
  assertNoError(error);
}

export async function updateDiscountCode(
  id: string,
  payload: Omit<DiscountCodeRow, 'id' | 'created_at'>
): Promise<void> {
  const { error } = await supabase.from('discount_codes').update(payload).eq('id', id);
  assertNoError(error);
}

export async function deleteDiscountCode(id: string): Promise<void> {
  const { error } = await supabase.from('discount_codes').delete().eq('id', id);
  assertNoError(error);
}

function normalizeDealRow(
  d: DealRow & { description?: string | null; image_url?: string | null; is_archived?: boolean; bundle_type?: BundleType; eligible_for_discount?: boolean }
): DealRow {
  return {
    ...d,
    description: d.description ?? '',
    image_url: d.image_url ? normalizeMenuItemImageUrl(d.image_url) : null,
    bundle_type: d.bundle_type === 'munch_box' ? 'munch_box' : 'deal',
    is_archived: d.is_archived ?? false,
    eligible_for_discount: d.eligible_for_discount ?? false,
  };
}

function normalizeDealSlotRow(
  s: DealSlotRow & { allowed_menu_item_ids?: string[] }
): DealSlotRow {
  return {
    ...s,
    quantity: Math.max(1, Number(s.quantity) || 1),
    display_order: Math.max(1, Number(s.display_order) || 1),
    allowed_menu_item_ids: s.allowed_menu_item_ids ?? [],
  };
}

async function attachDealSlots(dealRows: DealRow[]): Promise<DealWithSlots[]> {
  if (!dealRows.length) return [];
  const dealIds = dealRows.map((d) => d.id);
  const { data: slots, error: slotsError } = await supabase
    .from('deal_slots')
    .select('*')
    .in('deal_id', dealIds)
    .order('display_order');
  assertNoError(slotsError);
  const slotRows = (slots ?? []).map((s) => normalizeDealSlotRow(s as DealSlotRow));
  const slotIds = slotRows.map((s) => s.id);
  const allowedBySlot = new Map<string, string[]>();
  if (slotIds.length) {
    const { data: slotItems, error: slotItemsError } = await supabase
      .from('deal_slot_items')
      .select('slot_id, menu_item_id, display_order')
      .in('slot_id', slotIds)
      .order('display_order');
    assertNoError(slotItemsError);
    (slotItems ?? []).forEach((row) => {
      const slotId = row.slot_id as string;
      const list = allowedBySlot.get(slotId) ?? [];
      list.push(row.menu_item_id as string);
      allowedBySlot.set(slotId, list);
    });
  }
  const slotsByDeal = new Map<string, DealSlotRow[]>();
  slotRows.forEach((row) => {
    const withAllowed = normalizeDealSlotRow({
      ...row,
      allowed_menu_item_ids: allowedBySlot.get(row.id) ?? [],
    });
    const list = slotsByDeal.get(withAllowed.deal_id) ?? [];
    list.push(withAllowed);
    slotsByDeal.set(withAllowed.deal_id, list);
  });
  return dealRows.map((d) => ({ ...d, slots: slotsByDeal.get(d.id) ?? [] }));
}

export async function getDeals(): Promise<DealWithSlots[]> {
  const { data, error } = await supabase
    .from('deals')
    .select('*')
    .eq('is_archived', false)
    .eq('is_available', true)
    .eq('bundle_type', 'deal')
    .order('display_order')
    .order('name');
  assertNoError(error);
  return attachDealSlots((data ?? []).map((d) => normalizeDealRow(d as DealRow)));
}

export async function getMunchBoxes(): Promise<DealWithSlots[]> {
  const { data, error } = await supabase
    .from('deals')
    .select('*')
    .eq('is_archived', false)
    .eq('is_available', true)
    .eq('bundle_type', 'munch_box')
    .order('display_order')
    .order('name');
  assertNoError(error);
  return attachDealSlots((data ?? []).map((d) => normalizeDealRow(d as DealRow)));
}

export async function getAdminDeals(bundleType?: BundleType): Promise<DealWithSlots[]> {
  let query = supabase.from('deals').select('*').eq('is_archived', false).order('display_order').order('name');
  if (bundleType) {
    query = query.eq('bundle_type', bundleType);
  }
  const { data, error } = await query;
  assertNoError(error);
  return attachDealSlots((data ?? []).map((d) => normalizeDealRow(d as DealRow)));
}

export async function getDealById(id: string): Promise<DealWithSlots | null> {
  const { data, error } = await supabase
    .from('deals')
    .select('*')
    .eq('id', id)
    .eq('is_archived', false)
    .maybeSingle();
  assertNoError(error);
  if (!data) return null;
  const deal = normalizeDealRow(data as DealRow);
  const [withSlots] = await attachDealSlots([deal]);
  return withSlots ?? null;
}

export async function createDeal(payload: Omit<DealRow, 'id' | 'created_at' | 'updated_at' | 'is_archived'>): Promise<string> {
  const { data, error } = await supabase
    .from('deals')
    .insert({ ...payload, is_archived: false })
    .select('id')
    .single();
  assertNoError(error);
  if (!data?.id) throw new Error('Deal was not created.');
  return data.id as string;
}

export async function updateDeal(
  id: string,
  payload: Omit<DealRow, 'id' | 'created_at' | 'updated_at' | 'is_archived'>
): Promise<void> {
  const { error } = await supabase.from('deals').update(payload).eq('id', id);
  assertNoError(error);
}

export async function deleteDeal(id: string): Promise<void> {
  const { data: orderRefs, error: orderError } = await supabase
    .from('order_items')
    .select('id')
    .eq('deal_id', id)
    .limit(1);
  assertNoError(orderError);
  if (orderRefs?.length) {
    const { error } = await supabase.from('deals').update({ is_archived: true, is_available: false }).eq('id', id);
    assertNoError(error);
    return;
  }
  const { error: slotsError } = await supabase.from('deal_slots').delete().eq('deal_id', id);
  assertNoError(slotsError);
  const { error } = await supabase.from('deals').delete().eq('id', id);
  assertNoError(error);
}

export async function createDealSlot(
  payload: Omit<DealSlotRow, 'id' | 'created_at' | 'allowed_menu_item_ids'>
): Promise<string> {
  const { data, error } = await supabase.from('deal_slots').insert(payload).select('id').single();
  assertNoError(error);
  if (!data?.id) throw new Error('Deal slot was not created.');
  return data.id as string;
}

export async function updateDealSlot(
  id: string,
  payload: Omit<DealSlotRow, 'id' | 'deal_id' | 'created_at' | 'allowed_menu_item_ids'>
): Promise<void> {
  const { error } = await supabase.from('deal_slots').update(payload).eq('id', id);
  assertNoError(error);
}

export async function setDealSlotAllowedItems(slotId: string, menuItemIds: string[]): Promise<void> {
  const { error: deleteError } = await supabase.from('deal_slot_items').delete().eq('slot_id', slotId);
  assertNoError(deleteError);
  if (!menuItemIds.length) return;
  const rows = menuItemIds.map((menu_item_id, index) => ({
    slot_id: slotId,
    menu_item_id,
    display_order: index + 1,
  }));
  const { error } = await supabase.from('deal_slot_items').insert(rows);
  assertNoError(error);
}

export async function saveDealSlot(
  slotId: string | null,
  dealId: string,
  payload: Omit<DealSlotRow, 'id' | 'created_at' | 'allowed_menu_item_ids' | 'deal_id'>,
  allowedMenuItemIds: string[]
): Promise<string> {
  const slotPayload = { ...payload, deal_id: dealId };
  const allowed =
    payload.slot_type === 'choice' && allowedMenuItemIds.length ? allowedMenuItemIds : [];
  let id = slotId;
  if (id) {
    await updateDealSlot(id, payload);
  } else {
    id = await createDealSlot(slotPayload);
  }
  await setDealSlotAllowedItems(id, allowed);
  return id;
}

export async function reorderDealSlots(dealId: string, orderedSlotIds: string[]): Promise<void> {
  if (!orderedSlotIds.length) return;
  const results = await Promise.all(
    orderedSlotIds.map((id, index) =>
      supabase
        .from('deal_slots')
        .update({ display_order: index + 1 })
        .eq('id', id)
        .eq('deal_id', dealId)
    )
  );
  results.forEach(({ error }) => assertNoError(error));
}

export async function deleteDealSlot(id: string): Promise<void> {
  const { error } = await supabase.from('deal_slots').delete().eq('id', id);
  assertNoError(error);
}

export async function getAllOrdersSince(sinceIso: string): Promise<OrderRow[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .gte('created_at', sinceIso)
    .order('created_at', { ascending: false });
  assertNoError(error);
  return (data ?? []) as OrderRow[];
}

export async function getOrderItemsSince(sinceIso: string): Promise<OrderItemRowLite[]> {
  const { data, error } = await supabase
    .from('order_items')
    .select('id, order_id, menu_item_id, deal_id, quantity, price, selections, created_at')
    .gte('created_at', sinceIso);
  assertNoError(error);
  return (data ?? []) as OrderItemRowLite[];
}

export async function getOrderItemsByOrderIds(orderIds: string[]): Promise<OrderItemRowLite[]> {
  if (!orderIds.length) return [];
  const { data, error } = await supabase
    .from('order_items')
    .select('id, order_id, menu_item_id, deal_id, quantity, price, selections, created_at')
    .in('order_id', orderIds);
  assertNoError(error);
  return (data ?? []) as OrderItemRowLite[];
}

export async function getMenuItemNames(): Promise<Array<{ id: string; name: string }>> {
  const { data, error } = await supabase.from('menu_items').select('id, name').order('name');
  assertNoError(error);
  return (data ?? []) as Array<{ id: string; name: string }>;
}

export async function getDealNames(): Promise<Array<{ id: string; name: string; bundle_type: string }>> {
  const { data, error } = await supabase.from('deals').select('id, name, bundle_type').order('name');
  assertNoError(error);
  return (data ?? []) as Array<{ id: string; name: string; bundle_type: string }>;
}

const DEFAULT_MEAL_UPGRADE_PRICE = 1.5;

export async function getMealUpgradePrice(): Promise<number> {
  const { data, error } = await supabase.rpc('get_meal_upgrade_price');
  assertNoError(error);
  const price = Number(data);
  return Number.isFinite(price) && price >= 0 ? price : DEFAULT_MEAL_UPGRADE_PRICE;
}

export async function updateMealUpgradePrice(price: number): Promise<void> {
  const normalized = Math.max(0, Math.round(price * 100) / 100);
  const { error } = await supabase.from('settings').update({ meal_upgrade_price: normalized }).eq('id', 1);
  assertNoError(error);
}

export async function getActiveHeroPromo(): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_active_hero_promo');
  assertNoError(error);
  if (typeof data !== 'string') return null;
  const trimmed = data.trim();
  return trimmed ? trimmed : null;
}

export async function getHeroPromoSettings(): Promise<HeroPromoSettings> {
  const { data, error } = await supabase
    .from('settings')
    .select('hero_promo_text, hero_promo_start_at, hero_promo_end_at')
    .eq('id', 1)
    .maybeSingle();
  assertNoError(error);
  const row = data as {
    hero_promo_text?: string | null;
    hero_promo_start_at?: string | null;
    hero_promo_end_at?: string | null;
  } | null;
  return {
    text: row?.hero_promo_text?.trim() ?? '',
    startAt: row?.hero_promo_start_at ?? null,
    endAt: row?.hero_promo_end_at ?? null,
  };
}

export async function updateHeroPromoSettings(payload: HeroPromoSettings): Promise<void> {
  const text = payload.text.trim();
  const { error } = await supabase
    .from('settings')
    .update({
      hero_promo_text: text || null,
      hero_promo_start_at: text ? payload.startAt : null,
      hero_promo_end_at: text ? payload.endAt : null,
    })
    .eq('id', 1);
  assertNoError(error);
}

export async function getHeroDealImageUrl(): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_hero_deal_image_url');
  assertNoError(error);
  if (typeof data !== 'string') return null;
  const trimmed = data.trim();
  return trimmed ? normalizeMenuItemImageUrl(trimmed) : null;
}

export async function getHeroDealImageSettings(): Promise<HeroDealImageSettings> {
  const { data, error } = await supabase
    .from('settings')
    .select('hero_deal_image_url')
    .eq('id', 1)
    .maybeSingle();
  assertNoError(error);
  const row = data as { hero_deal_image_url?: string | null } | null;
  const raw = row?.hero_deal_image_url?.trim() ?? '';
  return { imageUrl: raw ? normalizeMenuItemImageUrl(raw) : null };
}

export async function updateHeroDealImageSettings(payload: HeroDealImageSettings): Promise<void> {
  const imageUrl = payload.imageUrl?.trim() || null;
  const { error } = await supabase
    .from('settings')
    .update({ hero_deal_image_url: imageUrl })
    .eq('id', 1);
  assertNoError(error);
}

export async function getMenuBackgroundImageUrl(): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_menu_background_image_url');
  assertNoError(error);
  if (typeof data !== 'string') return null;
  const trimmed = data.trim();
  return trimmed ? normalizeMenuItemImageUrl(trimmed) : null;
}

export async function getMenuBackgroundImageSettings(): Promise<MenuBackgroundImageSettings> {
  const { data, error } = await supabase
    .from('settings')
    .select('menu_background_image_url')
    .eq('id', 1)
    .maybeSingle();
  assertNoError(error);
  const row = data as { menu_background_image_url?: string | null } | null;
  const raw = row?.menu_background_image_url?.trim() ?? '';
  return { imageUrl: raw ? normalizeMenuItemImageUrl(raw) : null };
}

export async function updateMenuBackgroundImageSettings(payload: MenuBackgroundImageSettings): Promise<void> {
  const imageUrl = payload.imageUrl?.trim() || null;
  const { error } = await supabase
    .from('settings')
    .update({ menu_background_image_url: imageUrl })
    .eq('id', 1);
  assertNoError(error);
}

const DEFAULT_ORDERS_PAUSED_MESSAGE =
  "We're experiencing high volume right now and are not taking online orders. Please check back soon.";

export async function getOrdersPauseStatus(): Promise<OrdersPauseStatus> {
  const availability = await getOrderingAvailability();
  return {
    paused: availability.paused,
    message: availability.paused ? availability.message : null,
  };
}

export async function getOrderingAvailability(): Promise<OrderingAvailability> {
  const { data, error } = await supabase.rpc('get_ordering_availability');
  assertNoError(error);
  if (!data || typeof data !== 'object') {
    return { acceptingOrders: true, paused: false, outsideHours: false, message: null, reason: 'open' };
  }
  const row = data as {
    acceptingOrders?: boolean;
    paused?: boolean;
    outsideHours?: boolean;
    message?: string | null;
    reason?: string;
  };
  const paused = row.paused === true;
  const outsideHours = row.outsideHours === true;
  const acceptingOrders = row.acceptingOrders === true && !paused && !outsideHours;
  const reason: OrderingAvailability['reason'] = paused
    ? 'paused'
    : outsideHours
      ? 'outside_hours'
      : 'open';
  const message =
    typeof row.message === 'string' && row.message.trim()
      ? row.message.trim()
      : paused
        ? DEFAULT_ORDERS_PAUSED_MESSAGE
        : outsideHours
          ? DEFAULT_CLOSED_MESSAGE
          : null;
  return {
    acceptingOrders,
    paused,
    outsideHours,
    message: acceptingOrders ? null : message,
    reason,
  };
}

export async function getOrdersPauseSettings(): Promise<OrdersPauseSettings> {
  const { data, error } = await supabase
    .from('settings')
    .select('orders_paused, orders_paused_message, orders_paused_start_at, orders_paused_end_at')
    .eq('id', 1)
    .maybeSingle();
  assertNoError(error);
  const row = data as {
    orders_paused?: boolean;
    orders_paused_message?: string | null;
    orders_paused_start_at?: string | null;
    orders_paused_end_at?: string | null;
  } | null;
  return {
    paused: row?.orders_paused === true,
    message: row?.orders_paused_message?.trim() || DEFAULT_ORDERS_PAUSED_MESSAGE,
    startAt: row?.orders_paused_start_at ?? null,
    endAt: row?.orders_paused_end_at ?? null,
  };
}

export async function updateOrdersPauseSettings(payload: OrdersPauseSettings): Promise<void> {
  const message = payload.message.trim() || DEFAULT_ORDERS_PAUSED_MESSAGE;
  const { error } = await supabase
    .from('settings')
    .update({
      orders_paused: payload.paused,
      orders_paused_message: payload.paused ? message : null,
      orders_paused_start_at: payload.paused ? payload.startAt : null,
      orders_paused_end_at: payload.paused ? payload.endAt : null,
    })
    .eq('id', 1);
  assertNoError(error);
}

export async function getOpeningHoursSettings(): Promise<OpeningHoursSettings> {
  const { data, error } = await supabase
    .from('settings')
    .select('opening_hours, opening_hours_closed_message')
    .eq('id', 1)
    .maybeSingle();
  assertNoError(error);
  const row = data as {
    opening_hours?: unknown;
    opening_hours_closed_message?: string | null;
  } | null;
  return {
    hours: normalizeOpeningHours(row?.opening_hours),
    closedMessage: row?.opening_hours_closed_message?.trim() || DEFAULT_CLOSED_MESSAGE,
  };
}

export async function updateOpeningHoursSettings(payload: OpeningHoursSettings): Promise<void> {
  const hours = normalizeOpeningHours(payload.hours);
  const closedMessage = payload.closedMessage.trim() || DEFAULT_CLOSED_MESSAGE;
  const { error } = await supabase
    .from('settings')
    .update({
      opening_hours: hours,
      opening_hours_closed_message: closedMessage,
    })
    .eq('id', 1);
  assertNoError(error);
}

